'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  initializeOnboarding,
  completeWelcomeStepAction,
  updateAccountInfoAction,
  completeJobDescriptionStepAction,
  getOnboardingStatusAction,
} from '@/dal/onboarding/actions';
import type { OnboardingStep, UserOnboarding } from '@/lib/db/schema';
import { useUser } from './user-context';

interface OnboardingContextType {
  onboardingData: UserOnboarding | null;
  currentStep: OnboardingStep;
  isCompleted: boolean;
  completeStep: (step: string, data?: any) => Promise<void>;
  goBackStep: () => Promise<void>;
  initializeUser: () => Promise<void>;
  refreshOnboardingStatus: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const [onboardingData, setOnboardingData] = useState<UserOnboarding | null>(null);

  const refreshOnboardingStatus = async () => {
    try {
      const data = await getOnboardingStatusAction();
      setOnboardingData(data);
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    }
  };

  const initializeUser = async () => {
    try {
      await initializeOnboarding();
      await refreshOnboardingStatus();
    } catch (error) {
      console.error('Failed to initialize onboarding:', error);
    }
  };

  const completeStep = async (step: Omit<OnboardingStep, 'complete'>, data?: any) => {
    try {
      switch (step) {
        case 'welcome':
          await completeWelcomeStepAction(data?.selectedLocale);
          break;
        case 'account_setup':
          await updateAccountInfoAction({
            fullName: data.fullName,
            toneOfVoice: data.toneOfVoice,
            length: data.length,
          });
          break;
        case 'job_description':
          await completeJobDescriptionStepAction({ skipped: data?.skipped });
          break;
        default:
          throw new Error(`Unknown step: ${step}`);
      }

      await refreshOnboardingStatus();
    } catch (error) {
      console.error(`Failed to complete step ${step}:`, error);
      throw error;
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeOnboarding();
        await refreshOnboardingStatus();
      } catch (error) {
        console.error('Failed to initialize onboarding:', error);
      }
    };

    if (!user.isOnboardingCompleted) initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBackStep = async () => {
    try {
      const steps = ['welcome', 'account_setup', 'job_description'];
      const currentIndex = steps.indexOf(onboardingData?.currentStep || 'welcome');
      if (currentIndex > 0) {
        const previousStep = steps[currentIndex - 1];
        // Here we would call a server action to update the step back
        // For now, we'll just manually update the state
        setOnboardingData((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            currentStep: previousStep as OnboardingStep,
          };
        });
      }
    } catch (error) {
      console.error('Failed to go back:', error);
      throw error;
    }
  };

  const contextValue: OnboardingContextType = {
    onboardingData,
    currentStep: onboardingData?.currentStep || 'welcome',
    isCompleted: onboardingData?.isCompleted || false,
    completeStep,
    goBackStep,
    initializeUser,
    refreshOnboardingStatus,
  };

  return <OnboardingContext.Provider value={contextValue}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
